import React, { useState, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Folder,
  FolderGit2,
  ChevronRight,
  ChevronDown,
  Loader2,
  ArrowUp,
  Home,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { listFilesystem, type FilesystemEntry } from '@/lib/api';

interface DirectoryBrowserProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (paths: string[]) => void;
}

function DirectoryEntryRow({
  entry,
  depth,
  isExpanded,
  isSelected,
  onToggleExpand,
  onToggleSelect,
}: {
  entry: FilesystemEntry;
  depth: number;
  isExpanded: boolean;
  isSelected: boolean;
  onToggleExpand: (path: string) => void;
  onToggleSelect: (path: string) => void;
}): React.ReactElement {
  if (entry.isGitRepo) {
    return (
      <label
        className={`flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-surface-elevated ${
          isSelected ? 'bg-primary/10' : ''
        }`}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
      >
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => {
            onToggleSelect(entry.path);
          }}
          className="h-4 w-4 accent-primary"
        />
        <FolderGit2 className="h-4 w-4 shrink-0 text-success" />
        <span className="truncate text-text-primary">{entry.name}</span>
      </label>
    );
  }

  if (entry.isDirectory) {
    return (
      <button
        type="button"
        className="flex w-full items-center gap-2 rounded px-2 py-1 text-sm hover:bg-surface-elevated"
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
        onClick={() => {
          onToggleExpand(entry.path);
        }}
      >
        {isExpanded ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-text-tertiary" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-text-tertiary" />
        )}
        <Folder className="h-4 w-4 shrink-0 text-text-secondary" />
        <span className="truncate text-text-primary">{entry.name}</span>
      </button>
    );
  }

  // Non-directory files are not shown in the tree
  return <></>;
}

function DirectoryChildren({
  parentPath,
  depth,
  expandedDirs,
  selectedPaths,
  onToggleExpand,
  onToggleSelect,
}: {
  parentPath: string;
  depth: number;
  expandedDirs: Set<string>;
  selectedPaths: Set<string>;
  onToggleExpand: (path: string) => void;
  onToggleSelect: (path: string) => void;
}): React.ReactElement {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['filesystem', parentPath],
    queryFn: () => listFilesystem(parentPath),
    staleTime: 30_000,
  });

  if (isLoading) {
    return (
      <div
        className="flex items-center gap-2 px-2 py-1 text-sm text-text-tertiary"
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
      >
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        <span>Loading...</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="px-2 py-1 text-sm text-error" style={{ paddingLeft: `${depth * 16 + 8}px` }}>
        {error.message.length > 100 ? `${error.message.slice(0, 100)}...` : error.message}
      </div>
    );
  }

  const entries = data?.entries ?? [];
  const dirEntries = entries.filter(e => e.isDirectory || e.isGitRepo);

  if (dirEntries.length === 0) {
    return (
      <div
        className="px-2 py-1 text-sm text-text-tertiary italic"
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
      >
        No subdirectories
      </div>
    );
  }

  return (
    <>
      {dirEntries.map(entry => (
        <React.Fragment key={entry.path}>
          <DirectoryEntryRow
            entry={entry}
            depth={depth}
            isExpanded={expandedDirs.has(entry.path)}
            isSelected={selectedPaths.has(entry.path)}
            onToggleExpand={onToggleExpand}
            onToggleSelect={onToggleSelect}
          />
          {entry.isDirectory && !entry.isGitRepo && expandedDirs.has(entry.path) && (
            <DirectoryChildren
              parentPath={entry.path}
              depth={depth + 1}
              expandedDirs={expandedDirs}
              selectedPaths={selectedPaths}
              onToggleExpand={onToggleExpand}
              onToggleSelect={onToggleSelect}
            />
          )}
        </React.Fragment>
      ))}
    </>
  );
}

/** Collect all visible git repo paths from fetched query data. */
function collectVisibleRepos(
  parentPath: string,
  expandedDirs: Set<string>,
  queryClient: { getQueryData: (key: unknown[]) => unknown }
): string[] {
  const data = queryClient.getQueryData(['filesystem', parentPath]) as
    | { entries: FilesystemEntry[] }
    | undefined;
  if (!data) return [];

  const repos: string[] = [];
  for (const entry of data.entries) {
    if (entry.isGitRepo) {
      repos.push(entry.path);
    } else if (entry.isDirectory && expandedDirs.has(entry.path)) {
      repos.push(...collectVisibleRepos(entry.path, expandedDirs, queryClient));
    }
  }
  return repos;
}

export function DirectoryBrowser({
  open,
  onOpenChange,
  onSelect,
}: DirectoryBrowserProps): React.ReactElement {
  const [currentPath, setCurrentPath] = useState('~');
  const [selectedPaths, setSelectedPaths] = useState<Set<string>>(new Set());
  const [expandedDirs, setExpandedDirs] = useState<Set<string>>(new Set());
  const [pathInput, setPathInput] = useState('');

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedPaths(new Set());
      setExpandedDirs(new Set());
    }
  }, [open]);

  // Fetch root directory to resolve ~ and get the actual path for breadcrumbs
  const rootQuery = useQuery({
    queryKey: ['filesystem', currentPath],
    queryFn: () => listFilesystem(currentPath),
    staleTime: 30_000,
    enabled: open,
  });

  // If the home directory is empty (e.g. Docker), fall back to /mnt
  useEffect(() => {
    if (rootQuery.data && currentPath === '~' && rootQuery.data.entries.length === 0) {
      setCurrentPath('/mnt');
    }
  }, [rootQuery.data, currentPath]);

  // Resolved path from the server (parent of root entries, or currentPath)
  const resolvedPath = rootQuery.data?.parent ?? currentPath;

  // Build breadcrumb segments from the resolved path
  const pathSegments =
    resolvedPath === '/'
      ? [{ name: '/', fullPath: '/' }]
      : resolvedPath.split('/').reduce<{ name: string; fullPath: string }[]>((acc, segment, i) => {
          if (i === 0 && segment === '') {
            acc.push({ name: '/', fullPath: '/' });
          } else if (segment) {
            const parent = acc.length > 0 ? acc[acc.length - 1].fullPath : '';
            const fullPath = parent === '/' ? `/${segment}` : `${parent}/${segment}`;
            acc.push({ name: segment, fullPath });
          }
          return acc;
        }, []);

  const handleToggleExpand = useCallback((path: string) => {
    setExpandedDirs(prev => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  }, []);

  const handleToggleSelect = useCallback((path: string) => {
    setSelectedPaths(prev => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  }, []);

  const handleNavigate = useCallback((path: string) => {
    setCurrentPath(path);
    setExpandedDirs(new Set());
    setPathInput('');
  }, []);

  const handleGoUp = useCallback((): void => {
    const resolved = resolvedPath;
    if (resolved === '/') return;
    const parent = resolved.substring(0, resolved.lastIndexOf('/')) || '/';
    handleNavigate(parent);
  }, [resolvedPath, handleNavigate]);

  const handlePathSubmit = useCallback((): void => {
    const trimmed = pathInput.trim();
    if (trimmed) {
      handleNavigate(trimmed);
    }
  }, [pathInput, handleNavigate]);

  const handleSelectAll = useCallback(() => {
    // We need access to the query client to find visible repos
    // Instead, we use a simpler approach: collect from DOM isn't ideal,
    // so we pass through the tree data via the query cache
    const repos = collectVisibleRepos(currentPath, expandedDirs, {
      getQueryData: () => rootQuery.data,
    });
    // Also collect from expanded subdirectories by walking the expanded set
    // For a more complete approach, we leverage the query cache directly
    setSelectedPaths(prev => {
      const next = new Set(prev);
      for (const repo of repos) {
        next.add(repo);
      }
      return next;
    });
  }, [currentPath, expandedDirs, rootQuery.data]);

  const handleClearSelection = useCallback(() => {
    setSelectedPaths(new Set());
  }, []);

  const handleConfirm = useCallback(() => {
    onSelect([...selectedPaths]);
    onOpenChange(false);
  }, [selectedPaths, onSelect, onOpenChange]);

  const selectedCount = selectedPaths.size;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Browse Repositories</DialogTitle>
          <DialogDescription>Select git repositories to add as projects</DialogDescription>
        </DialogHeader>

        {/* Navigation toolbar */}
        <div className="flex items-center gap-1.5">
          <Button
            variant="outline"
            size="sm"
            className="h-8 px-2 shrink-0 gap-1"
            onClick={(): void => {
              handleNavigate('/');
            }}
            disabled={resolvedPath === '/'}
            title="Go to filesystem root"
          >
            <Home className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8 px-2 shrink-0 gap-1"
            onClick={handleGoUp}
            disabled={resolvedPath === '/'}
            title="Go to parent directory"
          >
            <ArrowUp className="h-3.5 w-3.5" />
            <span className="text-xs">Up</span>
          </Button>
          <form
            className="flex-1 flex gap-1"
            onSubmit={(e): void => {
              e.preventDefault();
              handlePathSubmit();
            }}
          >
            <Input
              value={pathInput}
              onChange={(e): void => {
                setPathInput(e.target.value);
              }}
              placeholder={resolvedPath}
              className="h-8 text-xs font-mono"
            />
            <Button
              type="submit"
              variant="outline"
              size="sm"
              className="h-8 shrink-0"
              disabled={!pathInput.trim()}
            >
              Go
            </Button>
          </form>
        </div>

        {/* Breadcrumb path */}
        <div className="flex items-center gap-1 overflow-x-auto text-xs">
          {pathSegments.map((segment, i) => (
            <React.Fragment key={segment.fullPath}>
              {i > 0 && <ChevronRight className="h-3 w-3 text-text-tertiary shrink-0" />}
              {i === pathSegments.length - 1 ? (
                <span className="text-text-primary font-medium truncate">
                  {segment.name === '/' ? 'Root' : segment.name}
                </span>
              ) : (
                <button
                  type="button"
                  className="text-text-secondary hover:text-text-primary hover:underline shrink-0"
                  onClick={(): void => {
                    handleNavigate(segment.fullPath);
                  }}
                >
                  {segment.name === '/' ? 'Root' : segment.name}
                </button>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Tree view */}
        <ScrollArea className="h-80 rounded-md border border-border">
          <div className="py-1">
            <DirectoryChildren
              parentPath={currentPath}
              depth={0}
              expandedDirs={expandedDirs}
              selectedPaths={selectedPaths}
              onToggleExpand={handleToggleExpand}
              onToggleSelect={handleToggleSelect}
            />
          </div>
        </ScrollArea>

        {/* Footer */}
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            <Button variant="ghost" size="xs" onClick={handleSelectAll}>
              Select All
            </Button>
            <Button variant="ghost" size="xs" onClick={handleClearSelection}>
              Clear
            </Button>
          </div>
          <span className="text-xs text-text-secondary">
            {selectedCount} {selectedCount === 1 ? 'repository' : 'repositories'} selected
          </span>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                onOpenChange(false);
              }}
            >
              Cancel
            </Button>
            <Button size="sm" disabled={selectedCount === 0} onClick={handleConfirm}>
              Add Selected ({selectedCount})
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
